import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useDispatch } from 'react-redux';

import Confirm from 'components/Confirm';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';

import './ConfirmSaveModal.scss';

const { block, element } = bem('confirm-save-modal');

const ConfirmSaveModal = ({ label, icon, title, cancelLabel, acceptLabel, children, onConfirmSave }) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const isOpen = useRef(true);

  useEffect(
    () => () => {
      isOpen.current = false;
    },
    []
  );

  const handleCancel = () => {
    if (!isSaving) {
      dispatch(closeModal());
    }
  };

  const handleConfirm = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      // The save hook reports failure through the editor's own error surface, so the dialog
      // closes either way instead of duplicating it.
      await onConfirmSave?.();
    } finally {
      // A confirm that opens the next dialog in a chain has already replaced this one, and closing
      // here would take that one down with it.
      if (isOpen.current) {
        setIsSaving(false);
        dispatch(closeModal());
      }
    }
  };

  return (
    <div className={block()} role="dialog" aria-modal="true" aria-label={label}>
      <Confirm
        className={element('content')}
        title={
          icon ? (
            <span className={element('title')}>
              <Isvg src={icon} className={element('title-icon')} />
              {title}
            </span>
          ) : (
            title
          )
        }
        cancelNode={<Message message={cancelLabel} />}
        acceptNode={<Message message={acceptLabel} />}
        acceptDisabled={isSaving}
        acceptWaiting={isSaving}
        onCancel={handleCancel}
        onRequestClose={handleCancel}
        onConfirm={handleConfirm}>
        <p className={element('line')}>{children}</p>
      </Confirm>
    </div>
  );
};

ConfirmSaveModal.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  title: PropTypes.node.isRequired,
  cancelLabel: intlMessage.isRequired,
  acceptLabel: intlMessage.isRequired,
  children: PropTypes.node,
  onConfirmSave: PropTypes.func,
};

export default ConfirmSaveModal;
